import { t } from '@lingui/macro';
import deepmerge from 'deepmerge';

import { APPLICANT_FIELD_NAME } from '../constants/fieldNames';
import { DOCUMENT_TYPE, dulInitialState } from '../constants/document';
import { Elk, ELKChild, Relative } from '../models/elk';
import { CITIZENSHIP } from '../constants/citizenship';
import { DocumentTypeValue } from '../constants/document';
import { i18n } from './i18n';
import { getId } from './getIds';
import { initialChildState } from './initialState';

const childrenSerializer = ({ children, relatives }: Elk) => {
  const {
    surname,
    name,
    patronymic,
    sex,
    snils,
    birthdate,
    isPatronymicAbsent,
  } = APPLICANT_FIELD_NAME;
  const noPatronymic = i18n._(t`Нет отчества`);

  const serializeChildren = !children.length
    ? {}
    : children
        .map((child: ELKChild) => {
          const {
            name: nameVal = '',
            surname: surnameVal = '',
            patronymic: patronymicVal = '',
            birthdate: birthdateVal = '',
            snils_number: snilsVal = '',
            gender: genderVal = '',
            birth_certificate_number: numberVal = '',
            birth_certificate_issued_by: issuerVal = '',
            birth_certificate_issued_at: dateOfIssueVal = '',
            item_id: id,
            birth_place: birthPlaceVal = '',
            foreign_birth_certificate_number: foreignNumberVal,
          } = child;

          const fullNameVal = [surnameVal, nameVal, patronymicVal]
            .filter((name) => !!name)
            .join(' ');

          let citizenshipVal: CITIZENSHIP | null = null;
          let documentTypeVal: DocumentTypeValue = null;
          if (foreignNumberVal) {
            documentTypeVal = DOCUMENT_TYPE.BIRTH_CERTIFICATE_INTERNATIONAL;
          }
          if (numberVal) {
            citizenshipVal = CITIZENSHIP.CITIZEN;
            documentTypeVal = DOCUMENT_TYPE.BIRTH_CERTIFICATE_RF;
          }
          const secondParentId = getId();

          const {
            passport_number: passportNumber,
            passport_issued_at: passportIssueDate,
            passport_issued_by: passportIssuer,
            passport_division_code: passportDivisionCode,
          } = relatives.find(({ item_id }) => item_id === id) || {
            passport_number: null,
          };
          if (passportNumber) {
            citizenshipVal = CITIZENSHIP.CITIZEN;
          }

          const dul = passportNumber
            ? deepmerge(
                dulInitialState,
                Object.assign(
                  {},
                  {
                    type: { value: DOCUMENT_TYPE.PASSPORT_RF, disabled: true },
                  },
                  passportNumber
                    ? { number: { value: passportNumber, disabled: true } }
                    : {},
                  passportIssueDate
                    ? {
                        issueDate: { value: passportIssueDate, disabled: true },
                      }
                    : {},
                  passportIssuer
                    ? { issuer: { value: passportIssuer, disabled: true } }
                    : {},
                  passportDivisionCode
                    ? {
                        divisionCode: {
                          value: passportDivisionCode,
                          disabled: true,
                        },
                      }
                    : {}
                )
              )
            : dulInitialState;

          return {
            ...initialChildState,
            hidden: true,
            fullName: fullNameVal,
            id,
            [name]: {
              value: nameVal,
              disabled: !!nameVal,
            },
            [surname]: {
              value: surnameVal,
              disabled: !!surnameVal,
            },
            [patronymic]: {
              value: patronymicVal || noPatronymic,
              disabled: true,
            },
            [isPatronymicAbsent]: { value: !patronymicVal },
            [birthdate]: {
              value: birthdateVal,
              disabled: !!birthdateVal,
            },
            [snils]: {
              value: snilsVal,
              disabled: !!snilsVal,
            },
            [sex]: {
              value: genderVal,
              disabled: !!genderVal,
            },
            citizenship: {
              value: citizenshipVal,
            },
            birthCertificate: {
              type: {
                value: documentTypeVal,
              },
              birthCertificate: true,
            },
            birthPlace: { value: birthPlaceVal, disabled: !!birthPlaceVal },
            secondParent: {
              ...initialChildState.secondParent,
              id: secondParentId,
            },
            dul,
          };
        })
        .reduce((next, child) => {
          return { ...next, [child.id]: child };
        }, {});

  return serializeChildren;
};

export default childrenSerializer;
